<script setup>
import { computed, defineProps } from 'vue';
import { useRoute } from 'vue-router';
import Icon from '../components/IconComponent.vue';

defineProps({
    navigate: {
        type: Function,
        required: true
    }
});

const route = useRoute();

const whatsappLink =
    'https://wa.me/2347080900495?text=Hello%2C%20my%20payment%20could%20not%20be%20completed%20and%20I%20need%20help%20resolving%20it.';

const reference = computed(() => route.query.reference || '—');
const errorCode = computed(() => route.query.code || 'TXN-DECLINED');
const reason = computed(
    () =>
        route.query.reason ||
        'Your payment could not be processed by the provider. This is usually a temporary issue with the card or network.'
);

const helpSteps = [
    {
        icon: 'credit-card',
        title: 'Check your payment details',
        description: 'Confirm your card number, expiry date and the CVV are entered correctly.'
    },
    {
        icon: 'lock-closed',
        title: 'Confirm available funds',
        description: 'Make sure your card has enough balance and is enabled for online transactions.'
    },
    {
        icon: 'refresh',
        title: 'Try again or use another method',
        description: 'Retry the payment, or switch to a different card or bank transfer.'
    }
];
</script>

<template>
    <main class="relative overflow-hidden bg-gray-50 py-16 md:py-24">
        <div class="absolute -top-24 -right-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none"></div>
        <div class="absolute -bottom-24 -left-24 w-72 h-72 bg-rose-200/40 rounded-full blur-3xl pointer-events-none"></div>

        <div class="container mx-auto px-4 relative z-10">
            <div v-reveal:up class="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div class="h-1.5 bg-gradient-to-r from-primary via-rose-500 to-primary"></div>

                <div class="p-8 md:p-12">
                    <div class="text-center">
                        <div class="relative w-20 h-20 mx-auto mb-6">
                            <span class="absolute inset-0 rounded-full bg-rose-500/20 animate-ping-slow"></span>
                            <span class="relative w-20 h-20 rounded-full bg-white border-2 border-rose-100 text-primary flex items-center justify-center shadow-lg shadow-rose-500/20">
                                <Icon name="x-circle" class-name="w-10 h-10" />
                            </span>
                        </div>

                        <span class="inline-flex items-center gap-2 bg-rose-50 text-primary border border-rose-100 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-4">
                            <span class="w-2 h-2 rounded-full bg-primary"></span>
                            Payment Not Completed
                        </span>

                        <h1 class="text-3xl md:text-4xl font-extrabold text-gray-900">Transaction Unsuccessful</h1>
                        <p class="text-gray-600 max-w-xl mx-auto mt-3">
                            We could not complete your transaction and no charge has been made to your account.
                        </p>
                    </div>

                    <div class="mt-10 bg-rose-50 border border-rose-100 rounded-2xl p-5 flex items-start gap-4 text-left">
                        <div class="bg-white p-2.5 rounded-xl flex-shrink-0 border border-rose-100">
                            <Icon name="alert-circle" class-name="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <p class="font-semibold text-gray-800">What went wrong</p>
                            <p class="text-sm text-gray-600 mt-1">{{ reason }}</p>
                        </div>
                    </div>

                    <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 text-left">
                        <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <dt class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Reference</dt>
                            <dd class="font-mono font-semibold text-gray-800 mt-1 break-all">{{ reference }}</dd>
                        </div>
                        <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <dt class="text-[11px] uppercase tracking-widest text-gray-400 font-semibold">Error Code</dt>
                            <dd class="font-mono font-semibold text-gray-800 mt-1 break-all">{{ errorCode }}</dd>
                        </div>
                        <div class="bg-rose-50 rounded-xl p-4 border border-rose-100 sm:col-span-2 flex items-center justify-between">
                            <dt class="text-[11px] uppercase tracking-widest text-primary font-semibold">Status</dt>
                            <dd class="inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                                <Icon name="x-circle" class-name="w-4 h-4" />
                                Failed
                            </dd>
                        </div>
                    </dl>

                    <div class="mt-10">
                        <h2 class="text-xs font-bold uppercase tracking-widest text-gray-400 mb-5 text-left">How to Fix It</h2>
                        <ul class="space-y-4 text-left">
                            <li v-for="step in helpSteps" :key="step.title" class="flex items-start gap-4">
                                <div class="bg-gray-100 p-2.5 rounded-xl flex-shrink-0">
                                    <Icon :name="step.icon" class-name="w-5 h-5 text-gray-700" />
                                </div>
                                <div>
                                    <p class="font-semibold text-gray-800">{{ step.title }}</p>
                                    <p class="text-sm text-gray-600">{{ step.description }}</p>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div class="mt-10 flex flex-col sm:flex-row gap-3">
                        <button
                            @click="navigate('scheduling')"
                            class="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-primary/20 hover:bg-primary/90 transition duration-300"
                        >
                            <Icon name="refresh" class-name="w-5 h-5" />
                            Try Again
                        </button>
                        <a
                            :href="whatsappLink"
                            target="_blank"
                            rel="noopener"
                            class="flex-1 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:bg-[#20ba5a] transition duration-300"
                        >
                            <Icon name="whatsapp" class-name="w-5 h-5" />
                            Contact Support
                        </a>
                    </div>

                    <div class="mt-6 text-center">
                        <button
                            @click="navigate('home')"
                            class="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-primary transition duration-300"
                        >
                            <Icon name="home" class-name="w-4 h-4" />
                            Return to Homepage
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<style scoped>
.animate-ping-slow {
    animation: ping-slow 2.2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes ping-slow {
    0% {
        transform: scale(1);
        opacity: 0.6;
    }
    75%,
    100% {
        transform: scale(1.6);
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .animate-ping-slow {
        animation: none;
        opacity: 0;
    }
}
</style>
